/**
 * passkeyClient.js — WebAuthn / Passkey client wrapper.
 *
 * Feature-flagged at the FE via `PASSKEYS_ENABLED` (launchFlags.js)
 * AND at the BE via the `/auth/passkeys/status` endpoint.  Even if the
 * FE flag is true, the BE flag wins.
 *
 * Hard rules:
 *  • Never logs raw credential payloads.
 *  • Never logs challenge bytes.
 *  • Surfaces capability detection (browser/device support).
 */
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";
import { apiClient } from "@/App";
import { PASSKEYS_ENABLED } from "@/config/launchFlags";

let cachedStatus = null;
let cachedStatusAt = 0;

export const passkeyCapability = {
  supported: () => browserSupportsWebAuthn(),
  conditionalUiSupported: async () => {
    try {
      return await browserSupportsWebAuthnAutofill();
    } catch {
      return false;
    }
  },
};

/**
 * Resolves whether the user can attempt a passkey flow right now.
 * Combines:
 *   - FE flag PASSKEYS_ENABLED
 *   - BE flag from /auth/passkeys/status (5 min cache)
 *   - Browser/device capability
 */
export async function passkeysAvailable() {
  if (!PASSKEYS_ENABLED) return { available: false, reason: "fe_flag_off" };
  if (!browserSupportsWebAuthn()) {
    return { available: false, reason: "browser_unsupported" };
  }
  const now = Date.now();
  if (!cachedStatus || now - cachedStatusAt > 5 * 60 * 1000) {
    try {
      const r = await apiClient.get("/auth/passkeys/status");
      cachedStatus = r.data;
      cachedStatusAt = now;
    } catch {
      return { available: false, reason: "be_unreachable" };
    }
  }
  if (!cachedStatus?.passkeys_enabled) {
    return {
      available: false,
      reason: "be_flag_off",
      message: cachedStatus?.message || "",
    };
  }
  return { available: true };
}


/**
 * Register a new passkey for the currently-authenticated user.
 * Throws on cancellation or failure — caller surfaces to the user via toast.
 */
export async function registerPasskey({ label } = {}) {
  const opts = await apiClient.post("/auth/passkeys/register/options");
  const { session_id, options } = opts.data;
  const credential = await startRegistration({ optionsJSON: options });
  const verify = await apiClient.post("/auth/passkeys/register/verify", {
    session_id,
    credential,
    label: label || "Passkey",
  });
  return verify.data;
}


/**
 * Sign in with a passkey.  Uses discoverable credentials — no username
 * field needed.  On success returns { user, session_token }.
 */
export async function loginWithPasskey({ keepLoggedIn = true, conditional = false } = {}) {
  const opts = await apiClient.post("/auth/passkeys/login/options");
  const { session_id, options } = opts.data;
  const credential = await startAuthentication({
    optionsJSON: options,
    useBrowserAutofill: conditional,
  });
  const verify = await apiClient.post("/auth/passkeys/login/verify", {
    session_id,
    credential,
    keep_logged_in: !!keepLoggedIn,
  });
  return verify.data;
}


/** Authenticated user listing of their own passkeys. */
export async function listMyPasskeys() {
  const r = await apiClient.get("/auth/passkeys/me");
  return r.data;
}


/** Revoke a passkey by its short id (surfaced from /me). */
export async function revokePasskey(credentialIdShort) {
  const safe = credentialIdShort.replace(/…/g, "").replace(/\.\.\.$/, "");
  const r = await apiClient.delete(`/auth/passkeys/${encodeURIComponent(safe)}`);
  return r.data;
}
